/**
 * Patient medication-concierge client (M12-T06a, ADR-0028). Submit a request for
 * a catalog drug (OTP-verified patient) and read its waiting state. The curated
 * offer + pickup accept are T06b.
 */
import { apiFetch } from '../lib/api'

export type Urgency = 'same_day' | '24h' | 'routine'

export type MedicationRequestInput = {
  drugSlug: string
  quantity: string
  area?: string
  gpsLat?: number
  gpsLng?: number
  urgency: Urgency
}

export type MedicationRequestCreated = {
  reference: string
  status: string
  expires_at: string
}

/** POST /api/v1/patient/medication-requests — needs a verified patient `token`. */
export async function submitMedicationRequest(
  token: string,
  input: MedicationRequestInput,
): Promise<MedicationRequestCreated> {
  const body = await apiFetch<{ data: MedicationRequestCreated }>(
    '/api/v1/patient/medication-requests',
    {
      method: 'POST',
      token,
      body: {
        drug_slug: input.drugSlug,
        quantity: input.quantity,
        urgency: input.urgency,
        ...(input.area ? { area: input.area } : {}),
        ...(input.gpsLat != null ? { gps_lat: input.gpsLat } : {}),
        ...(input.gpsLng != null ? { gps_lng: input.gpsLng } : {}),
      },
    },
  )
  return body.data
}

/** The one curated offer AMTC sent (T06b). Pickup only in this task. */
export type PatientOffer = {
  reference: string
  hospital_slug: string
  hospital_name: string
  city: string
  hospital_lat: number
  hospital_lng: number
  medicine_price_pesewas: number
  fulfilment: string
  status: string
}

export type PatientMedicationRequest = {
  reference: string
  status: string
  quantity: string
  area: string | null
  urgency: Urgency
  drug_generic_name: string
  drug_brand_name: string | null
  drug_strength: string
  drug_form: string
  prescription_only: boolean
  expires_at: string
  created_at: string
  offer: PatientOffer | null
}

export function medicationRequestKey(reference: string) {
  return ['medication-request', reference] as const
}

/** GET /api/v1/patient/medication-requests/{ref} — the patient's own request
 * plus the curated offer once AMTC sends one. */
export async function fetchMedicationRequest(
  token: string,
  reference: string,
  signal?: AbortSignal,
): Promise<PatientMedicationRequest> {
  const opts = signal ? { token, signal } : { token }
  const body = await apiFetch<{ data: PatientMedicationRequest }>(
    `/api/v1/patient/medication-requests/${reference}`,
    opts,
  )
  return body.data
}

export type OfferAction = { reference: string; status: string }

/** POST /api/v1/patient/medication-requests/{ref}/accept-pickup — pay at the
 * pharmacy (off-gateway, ADR-0017); no online charge. */
export async function acceptPickup(
  token: string,
  reference: string,
): Promise<OfferAction> {
  const body = await apiFetch<{ data: OfferAction }>(
    `/api/v1/patient/medication-requests/${reference}/accept-pickup`,
    { method: 'POST', token },
  )
  return body.data
}

/** POST /api/v1/patient/medication-requests/{ref}/decline — back to the AMTC
 * queue for the next-best offer. */
export async function declineOffer(
  token: string,
  reference: string,
): Promise<OfferAction> {
  const body = await apiFetch<{ data: OfferAction }>(
    `/api/v1/patient/medication-requests/${reference}/decline`,
    { method: 'POST', token },
  )
  return body.data
}
